/**
 * patient-documents.service.ts
 *
 * Patient "Documents" tab (legacy parity). The list is the patient's
 * document activity log (comm_log type 4): Date / Subject / File / User.
 * Files are streamed from the docstore via the download endpoint.
 */
import { apiClient } from './apiClient';
import { isPreviewableFilename } from '@/lib/previewableFiles';
import type { ApiResponse } from '@/types/api.types';

export interface PatientDocument {
  commLogId: number;
  date: string | null;
  time: string;
  subject: string;
  fileName: string;
  userId: number;
  userName: string;
  planId: number;
}

export interface DocumentLibraryItem {
  documentId: number;
  title: string;
  description: string;
  fileName: string;
  format: string;
  categoryId: number;
  categoryName: string;
}

export interface DocumentPacketItem {
  packetId: number;
  name: string;
  description: string;
  format: string;
}

export interface DocumentCategoryItem {
  categoryId: number;
  name: string;
}

export interface DocumentLibrary {
  documents: DocumentLibraryItem[];
  packets: DocumentPacketItem[];
  categories: DocumentCategoryItem[];
}

/**
 * List a patient's logged documents (newest first).
 */
export function listPatientDocuments(patientId: number): Promise<PatientDocument[]> {
  return apiClient
    .get<ApiResponse<PatientDocument[]>>(`/patients/${patientId}/documents`)
    .then((r) => r.data.data);
}

/**
 * Fetch the document library (account documents, packets, categories) that
 * can be added to this patient.
 */
export function getDocumentLibrary(
  patientId: number,
  categoryId?: number,
): Promise<DocumentLibrary> {
  return apiClient
    .get<ApiResponse<DocumentLibrary>>(`/patients/${patientId}/documents/library`, {
      params: categoryId ? { categoryId } : undefined,
    })
    .then((r) => r.data.data);
}

/**
 * Add a library document to the patient's document log (comm_log type 4).
 */
export function addPatientDocument(
  patientId: number,
  documentId: number,
  planId?: number,
): Promise<PatientDocument> {
  return apiClient
    .post<ApiResponse<PatientDocument>>(`/patients/${patientId}/documents`, {
      documentId,
      planId: planId ?? 0,
    })
    .then((r) => r.data.data);
}

function downloadPath(patientId: number, fileName: string, inline: boolean): string {
  const params = new URLSearchParams({ file: fileName, inline: inline ? '1' : '0' });
  return `/patients/${patientId}/documents/download?${params.toString()}`;
}

/**
 * Fetch a document as a blob and return an object URL (for inline preview).
 * Returns null if the file is missing/empty. Caller is responsible for
 * revoking the URL when done.
 */
export async function fetchDocumentViewUrl(
  patientId: number,
  fileName: string,
): Promise<string | null> {
  try {
    const response = await apiClient.get(downloadPath(patientId, fileName, true), {
      responseType: 'blob',
    });
    const blob = response.data as Blob;
    if (blob.size === 0) return null;
    if (blob.type === 'application/json') return null;
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

/**
 * Open a document: previewable types open in a new tab, everything else (e.g.
 * Dymo `.label` files) downloads.
 */
export async function openDocument(patientId: number, fileName: string): Promise<void> {
  if (!isPreviewableFilename(fileName)) {
    await downloadDocument(patientId, fileName);
    return;
  }
  const url = await fetchDocumentViewUrl(patientId, fileName);
  if (!url) {
    await downloadDocument(patientId, fileName);
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
  // Revoke after a delay so the new tab has time to load the blob.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Force-download a document to disk.
 */
export async function downloadDocument(patientId: number, fileName: string): Promise<void> {
  const response = await apiClient.get(downloadPath(patientId, fileName, false), {
    responseType: 'blob',
  });
  const blob = response.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
